import {
  RegistrationFlow,
  LoginFlow,
  SettingsFlow,
  VerificationFlow,
  RecoveryFlow,
} from "@ory/kratos-client";
import { AxiosResponse } from "axios";
import {
  ReadonlyURLSearchParams,
  useRouter,
  useSearchParams,
} from "next/navigation";
import {
  useState,
  useCallback,
  useEffect,
  SetStateAction,
  Dispatch,
} from "react";

import { handleFlowError } from "./errors";

export type FlowType =
  "login" | "registration" | "settings" | "recovery" | "verification";

export type Flow =
  RegistrationFlow | LoginFlow | SettingsFlow | VerificationFlow | RecoveryFlow;

/**
 *
 * @param flowType Which flow type to give to the error handler
 * @param getExtraParams function that reads the extra parameters needed for the flow from the url parameters
 * @param get function that calls the kratos api to get the flow
 * @param create function that calls the kratos API to create the flow
 * @returns Flow get and setter
 */
export function useFlow<TFlow extends Flow, TParams>(
  flowType: FlowType,
  getExtraParams: (params: ReadonlyURLSearchParams | null) => TParams,
  get: (params: {
    readonly id: string;
  }) => Promise<AxiosResponse<TFlow, unknown>>,
  create: (params: TParams) => Promise<AxiosResponse<TFlow, unknown>>,
): [TFlow | undefined, Dispatch<SetStateAction<TFlow | undefined>>] {
  const router = useRouter();
  const [flow, setFlow] = useState<TFlow>();

  const params = useSearchParams();

  const flowId = params?.get("flow") ?? undefined;
  const extraParams = getExtraParams(params);

  const getFlow = useCallback(
    (flowId: string) => {
      get({ id: String(flowId) })
        .then(({ data }) => setFlow(data))
        .catch(handleFlowError(router, flowType, setFlow));
    },
    [router, get, flowType],
  );

  const createFlow = useCallback(
    (params: TParams) => {
      create(params)
        .then(({ data }) => {
          setFlow(data);
          router.push(`?flow=${data.id}`);
        })
        .catch(handleFlowError(router, flowType, setFlow));
    },
    [router, create, flowType],
  );

  useEffect(() => {
    if (flow) {
      return;
    }
    if (flowId) {
      void getFlow(flowId);
      return;
    }
    void createFlow(extraParams);
  });

  return [flow, setFlow];
}
